import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CaretLeft, CaretRight } from '@phosphor-icons/react';

export const Lightbox = ({ images, currentIndex, isOpen, onClose, onPrev, onNext, language }) => {
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') onPrev();
    if (e.key === 'ArrowRight') onNext();
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !images[currentIndex]) return null;

  const current = images[currentIndex];
  const title = language === 'fr' ? current.title_fr : current.title_en;
  const category = language === 'fr' ? current.category_fr : current.category_en;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center"
          data-testid="lightbox-overlay"
          onClick={onClose}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/95 backdrop-blur-sm" />

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 z-10 w-12 h-12 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            data-testid="lightbox-close"
          >
            <X size={24} className="text-white" weight="bold" />
          </button>

          {/* Counter */}
          <div className="absolute top-6 left-6 z-10 text-white/60 text-sm font-medium tracking-wider" data-testid="lightbox-counter">
            {currentIndex + 1} / {images.length}
          </div>

          {/* Prev Button */}
          {images.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); onPrev(); }}
              className="absolute left-4 md:left-8 z-10 w-12 h-12 bg-white/10 hover:bg-[#FF6B00] flex items-center justify-center transition-colors"
              data-testid="lightbox-prev"
            >
              <CaretLeft size={24} className="text-white" weight="bold" />
            </button>
          )}

          {/* Next Button */}
          {images.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); onNext(); }}
              className="absolute right-4 md:right-8 z-10 w-12 h-12 bg-white/10 hover:bg-[#FF6B00] flex items-center justify-center transition-colors"
              data-testid="lightbox-next"
            >
              <CaretRight size={24} className="text-white" weight="bold" />
            </button>
          )}

          {/* Image */}
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="relative z-10 max-w-5xl w-full mx-4 md:mx-8"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={current.image}
              alt={title}
              className="w-full max-h-[75vh] object-contain"
              data-testid="lightbox-image"
            />
            {/* Caption */}
            <div className="mt-4 text-center">
              <p className="text-[#FF6B00] text-xs font-medium uppercase tracking-[0.15em] mb-1">
                {category}
              </p>
              <h3 className="text-xl font-semibold text-white tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {title}
              </h3>
              <p className="text-sm text-white/50 mt-1">
                {current.client ? `${current.client} - ` : ''}{current.location}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
